import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosInstance';
import { useNavigate, useParams } from 'react-router-dom';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import CommonForm from '../../components/commonComponent/CommonForm';
import { validateForm } from '../../utils/formUtils';

const Eventcreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState({
    name: '',
    cluster: [],
    chapter: [],
    venue: '',
    type: '',
    startDate: '',
    endDate: '',
    formTime: '',
    endTime: '',
    description: '',
    guestAllow: false,
    guestFees: 0,
    lastRegistrationDate: '',
    lastRegistrationTime: '',
    reportingTime: '',
    image: '',
    notes: '',
    // colorCode: '#000000',
  });

  const [dropdowns, setDropdowns] = useState({
    clusters: [],
    chapters: [],
    venues: [],
    eventTypes: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchDropdowns = async () => {
    try {
      const [clusterRes, chapterRes, venueRes, eventTypeRes] = await Promise.all([
        axios.get('/admin/api/clusters/all'),
        axios.get('/admin/api/chapters/all'),
        axios.get('/admin/api/venues'),
        axios.get('/admin/api/event-type'),
      ]);

      setDropdowns({
        clusters: clusterRes.data?.cluster.map(c => ({ value: c._id, label: c.name })) || [],
        chapters: chapterRes.data?.chapter.map(ch => ({ value: ch._id, label: ch.name })) || [],
        venues: venueRes.data?.venues.map(v => ({ value: v._id, label: v.venueName || v.name })) || [],
        eventTypes: eventTypeRes.data?.eventTypes.map(t => ({ value: t._id, label: t.name })) || [],
      });
    } catch (error) {
      toastError('Failed to load dropdown data');
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchEvent = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/event/${id}`);
        const data = res.data.event;

        setFormValues({
          name: data.Name || data.name || '',
          cluster: data.cluster?.map(c => c._id) || [],
          chapter: data.chapter?.map(c => c._id) || [],
          venue: data.meetingVenue?._id || data.venue?._id || '',
          type: data.meetingType?._id || data.type?._id || '',
          startDate: data.startDate?.split('T')[0] || '',
          endDate: data.endDate?.split('T')[0] || '',
          formTime: data.formTime || '',
          endTime: data.endTime || '',
          description: data.description || '',
          guestAllow: data.guestAllow || false,
          guestFees: Number(data.guestFees) || 0,
          lastRegistrationDate: data.lastRegistrationDate?.split('T')[0] || '',
          lastRegistrationTime: data.lastRegistrationTime || '',
          reportingTime: data.reportingTime || '',
          image: data.meetingImage || data.image || '',
          notes: data.notes || '',
          // colorCode: data.colorCode || '#000000',
        });
      } catch (error) {
        toastError('Failed to load event');
        navigate('/event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const handleSubmit = async () => {
    const validationRules = {
      name: { required: true },
      chapter: { required: true },
      venue: { required: true },
      type: { required: true },
      startDate: { required: true },
      endDate: { required: true },
      formTime: { required: true },
      endTime: { required: true },
      lastRegistrationDate: { required: true },
      lastRegistrationTime: { required: true },
    };

    const validationErrors = validateForm(formValues, validationRules);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const formData = new FormData();

      formData.append('name', formValues.name || '');
      formData.append('venue', formValues.venue || '');
      formData.append('type', formValues.type || '');
      formData.append('description', formValues.description || '');
      formData.append('startDate', formValues.startDate || '');
      formData.append('endDate', formValues.endDate || '');
      formData.append('formTime', formValues.formTime || '');
      formData.append('endTime', formValues.endTime || '');
      formData.append('lastRegistrationDate', formValues.lastRegistrationDate || '');
      formData.append('lastRegistrationTime', formValues.lastRegistrationTime || '');
      formData.append('reportingTime', formValues.reportingTime || '');
      formData.append('guestAllow', formValues.guestAllow);
      formData.append('guestFees', formValues.guestFees || 0);
      formData.append('notes', formValues.notes || '');
      // formData.append('colorCode', formValues.colorCode || '#000000');

      if (formValues.image && typeof formValues.image === 'object') {
        formData.append('meetingImage', formValues.image);
      }

      if (Array.isArray(formValues.cluster)) {
        formData.append('cluster', formValues.cluster.join(','));
      }

      if (Array.isArray(formValues.chapter)) {
        formData.append('chapter', formValues.chapter.join(','));
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (id) {
        await axios.put(`/api/event/${id}`, formData, config);
        toastSuccess('Event updated successfully');
      } else {
        await axios.post('/api/event', formData, config);
        toastSuccess('Event created successfully');
      }

      navigate('/event');
    } catch (error) {
      toastError('Failed to save event');
    }
  };

  const fieldSections = [
    {
      section: 'Event Details',
      fields: [
        { name: 'name', label: 'Event Name', type: 'text', required: true },
        { name: 'type', label: 'Event Type', type: 'select', options: dropdowns.eventTypes, required: true },
        { name: 'description', label: 'Description', type: 'textarea', rows: 3 },
        { name: 'image', label: 'Event Image', type: 'file' },
      ]
    },
    {
      section: 'Location & Participants',
      fields: [
        { name: 'cluster', label: 'Cluster', type: 'select', isMulti: true, options: dropdowns.clusters },
        { name: 'chapter', label: 'Chapter', type: 'select', isMulti: true, options: dropdowns.chapters, required: true },
        { name: 'venue', label: 'Venue', type: 'select', options: dropdowns.venues, required: true },
      ]
    },
    {
      section: 'Schedule & Timing',
      fields: [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true },
        { name: 'endDate', label: 'End Date', type: 'date', required: true },
        { name: 'formTime', label: 'From Time', type: 'time', required: true },
        { name: 'endTime', label: 'To Time', type: 'time', required: true },
        { name: 'reportingTime', label: 'Reporting Time', type: 'time', required: true },
      ]
    },
    {
      section: 'Registration Settings',
      fields: [
        { name: 'lastRegistrationDate', label: 'Last Registration Date', type: 'date', required: true },
        { name: 'lastRegistrationTime', label: 'Last Registration Time', type: 'time', required: true },
        { name: 'guestAllow', label: 'Allow Guest?', type: 'checkbox' },
        ...(formValues.guestAllow ? [{ name: 'guestFees', label: 'Guest Fees', type: 'number' }] : []),
        { name: 'notes', label: 'Notes', type: 'textarea', rows: 3, placeholder: 'Additional notes or instructions for the event...' },
      ]
    }
  ];

  if (loading) return <div className="text-center py-4">Loading event...</div>;

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0 text-dark">{id ? 'Edit Event' : 'Create New Event'}</h3>
            <button 
              onClick={() => navigate('/event')} 
              className="btn btn-outline-secondary"
            >
              Back to Events
            </button>
          </div>

          {/* Current Image Preview */}
          {formValues.image && typeof formValues.image === 'string' && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0">Current Event Image</h6>
              </div>
              <div className="card-body text-center">
                <img 
                  src={formValues.image} 
                  alt="Current Event" 
                  className="img-fluid rounded shadow-sm"
                  style={{ maxHeight: '200px' }} 
                />
              </div>
            </div>
          )}

          {/* Form Sections */}
          <CommonForm
            title=""
            fields={fieldSections}
            values={formValues}
            onChange={(updated) => setFormValues((prev) => ({ ...prev, ...updated }))}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/event')}
            errors={errors}
            requiredFields={['name', 'chapter', 'venue', 'type', 'startDate', 'endDate', 'formTime', 'endTime', 'lastRegistrationDate', 'lastRegistrationTime', 'reportingTime']}
            submitLabel={id ? 'Update Event' : 'Create Event'}
            cancelLabel="Cancel"
          />
        </div>
      </div>
    </div>
  );
};

export default Eventcreate;
